/**
 * The wallet account connected in this browser right now, which is NOT the
 * signed-in operator wallet.
 *
 * The session belongs to the operator wallet that signed the sign-in message.
 * The connected account only decides who SENDS an on-chain change: an
 * operator signs in, then switches the wallet's account (or picks another
 * wallet) to the executor's owner or to the treasury, and stays signed in
 * throughout. So nothing here ends the session: an account switch only
 * updates what the pages show and who may press "Send from wallet".
 *
 * The account is always the PICKED wallet's (`wallet.ts`), and `null` when
 * none is reachable (no usable pick, locked, or this site not authorised yet):
 * pages then offer `connectWallet()` or `pickWallet()`, called straight from
 * the click.
 */

import { useEffect, useReducer, useSyncExternalStore } from "react";
import * as wallet from "./wallet";

let current: string | null = null;
const listeners = new Set<() => void>();

/**
 * The signer taken from the wallet's account list in a review, and the
 * wallet's first account at that moment. A wallet re-announces its accounts,
 * last selected first, right after a permission change, which would otherwise
 * undo the choice at once.
 */
let choice: { account: string; first: string | null } | null = null;

function emit(): void {
  for (const listener of [...listeners]) listener();
}

function record(address: string | null): void {
  const next = address === null ? null : address.toLowerCase();
  if (next === current) return;
  current = next;
  emit();
}

/** The connected account, lowercase, or null. */
export function getConnectedAccount(): string | null {
  return current;
}

/** Records the connected account (lowercased); notifies subscribers on change. A connect replaces any account taken from the list. */
export function setConnectedAccount(address: string | null): void {
  choice = null;
  record(address);
}

/** The account a list from the wallet makes connected: the taken signer while the list holds it and starts as it did, else the first. */
function follow(accounts: readonly string[]): string | null {
  const list = accounts.map((a) => a.toLowerCase());
  const first = list[0] ?? null;
  if (choice !== null && list.includes(choice.account) && first === choice.first) return choice.account;
  choice = null;
  return first;
}

/** What the wallet's account list gave a review: the signer, or another account (null: none). */
export type AccountChoice = { kind: "chosen"; account: string } | { kind: "not-permitted"; given: string | null };

/**
 * "Use the treasury wallet": the picked wallet's account list, then the
 * signer as the connected account when the wallet lets this site use it, even
 * when it is not listed first. Call it straight from the click. Rejects as
 * `wallet.requestAccountChoice` does (4001 silent).
 */
export function chooseAccount(signer: string): Promise<AccountChoice> {
  const rdns = wallet.getSelectedRdns();
  return wallet.requestAccountChoice().then((accounts): AccountChoice => {
    // Another wallet was picked while the list was open: its answer is about the previous one.
    if (wallet.getSelectedRdns() !== rdns) throw new wallet.ConnectionCancelled();
    const list = accounts.map((a) => a.toLowerCase());
    const want = signer.toLowerCase();
    if (list.includes(want)) {
      choice = { account: want, first: list[0] ?? null };
      record(want);
      return { kind: "chosen", account: want };
    }
    choice = null;
    record(list[0] ?? null);
    return { kind: "not-permitted", given: list[0] ?? null };
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React view of the connected account. */
export function useConnectedAccount(): string | null {
  return useSyncExternalStore(subscribe, getConnectedAccount, getConnectedAccount);
}

/**
 * Reads the picked wallet's authorised accounts WITHOUT prompting and records
 * the first. Never rejects. An answer that lands after the pick changed is
 * about the previous wallet and is dropped.
 */
export async function refreshConnectedAccount(): Promise<string | null> {
  const rdns = wallet.getSelectedRdns();
  const accounts = await wallet.getAccounts();
  if (wallet.getSelectedRdns() === rdns) record(follow(accounts));
  return current;
}

/**
 * Asks the PICKED wallet to connect (`explicit`: the rdns just picked from the
 * list) and records the account. Call it synchronously from the click handler
 * (no `await` before it). Rejects with a
 * `WalletError`: `NO_WALLET_CHOSEN` when nothing usable is picked (offer the
 * list), 4001 for the user's own refusal (never reported).
 */
export function connectWallet(explicit?: string): Promise<string> {
  const rdns = wallet.getSelectedRdns();
  return wallet.connect(explicit).then((address) => {
    if (wallet.getSelectedRdns() === rdns) setConnectedAccount(address);
    return address;
  });
}

/**
 * The operator picked `rdns` from the list: remember it, so every later call
 * goes to that wallet, and connect it. Synchronous up to the wallet's prompt,
 * so call it straight from the click. Another wallet than before drops the
 * previous wallet's account at once.
 */
export function pickWallet(rdns: string): Promise<string> {
  if (wallet.getSelectedRdns() !== rdns) {
    wallet.selectProvider(rdns);
    setConnectedAccount(null);
  }
  // Named, so WalletConnect knows the operator asked for it (an explicit pick is kept even when refused).
  return connectWallet(rdns);
}

/**
 * "Use another wallet": when the wallet is WalletConnect, its pairing is hung
 * up first (the phone stops showing a session for this browser, and the
 * remembered pick goes), then the caller shows the list, where WalletConnect
 * pairs afresh. An extension has no session to end: nothing changes until
 * another wallet is picked.
 */
export function releaseWallet(): void {
  if (wallet.getSelectedRdns() !== wallet.WALLETCONNECT_RDNS) return;
  wallet.disconnect();
  setConnectedAccount(null);
}

/**
 * Keeps the connected account current while a screen is open (the shell and
 * the sign-in card): re-read, and re-subscribed to the picked wallet's
 * `accountsChanged`, whenever a wallet announces itself late, the pick
 * changes, or a WalletConnect session is paired, restored, or ends (on the
 * phone, or by expiry: the account is then gone). An account change never
 * signs the operator out.
 */
export function useWalletAccountTracking(): void {
  const [epoch, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const offProviders = wallet.onProvidersChanged(bump);
    const offSelection = wallet.onSelectionChanged(bump);
    const offWalletConnect = wallet.onWalletConnectChanged(bump);
    return () => {
      offProviders();
      offSelection();
      offWalletConnect();
    };
  }, []);

  useEffect(() => {
    void refreshConnectedAccount();
    return wallet.onAccountsChanged((accounts) => record(follow(accounts)));
  }, [epoch]);
}
